'use client'

import React from 'react'

import './button.scss'

import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

type Props<T extends React.ElementType> = Overwrite<
  CommonProps<T>,
  {
    as?: T
    children?: React.ReactNode
    icon?: React.ReactElement<{title?: string}>
    iconAlign?: 'left' | 'right'
    size?: '90' | '100' | '200' | '300'
    block?: boolean
    disabled?: boolean
    onClick?: (event: React.MouseEvent, href: string) => void
    href?: string
    appearance?: 'base' | 'error'
    hierarchy?: 'primary' | 'secondary' | 'ghost' | 'ghost-input-action'
  }
>

const Button = <T extends React.ElementType = 'button'>({
  className,
  children,
  as,
  href = '#',
  icon,
  iconAlign = 'left',
  size = '100',
  appearance = 'base',
  hierarchy = 'primary',
  block = false,
  disabled = false,
  onClick,
  ...props
}: Props<T>) => {
  const Root = (as ?? 'button') as React.ElementType

  const iconOnly = !!icon && !children

  const handleClick: React.MouseEventHandler = event => {
    if (onClick) {
      onClick(event, href)
    }
  }

  return (
    <Root
      type={Root === 'button' ? 'button' : undefined}
      {...props}
      className={cn(
        'Button',
        {
          [`size-${size}`]: !!size,
          [`appearance-${appearance}`]: !!appearance,
          [`hierarchy-${hierarchy}`]: !!hierarchy,
          block,
          disabled,
          iconOnly,
        },
        className
      )}
      disabled={Root === 'button' ? disabled : undefined}
      aria-disabled={Root !== 'button' ? disabled : undefined}
      aria-label={iconOnly ? icon.props.title : undefined}
      onClick={onClick ? handleClick : undefined}
      href={href}
    >
      {icon && iconAlign === 'left' && <span className="Button__icon">{icon}</span>}
      {children && <span className="Button__label">{children}</span>}
      {icon && iconAlign === 'right' && <span className="Button__icon">{icon}</span>}
    </Root>
  )
}

export default Button
